import SearchIcon from "@mui/icons-material/Search";
import {
	Button,
	Grid2,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import type {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	GridReadyEvent,
	IRowNode,
	MenuItemDef,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "../../../../account";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../components/shared/CenteredLazyLoading";
import { useContextMenuMESF } from "../../../../controls/contextMenu";
import { themeDXT } from "../../../../css/themeMESF";
import { RecipientTypeLegend } from "../../emailTopics/components/RecipientTypeChips";
import type { EmailAddress } from "../../emailTopics/models/EmailAddress";
import { useEmailAddresses } from "../EmailAddressesPage";
import { UpsertDeleteModal } from "./UpsertDeleteModal";
import { useEmailAddressesOptionsFunctions } from "./customHooks/useEmailAddressesOptionsFunctions";
import { useTableEmailAddresses } from "./customHooks/useTableEmailAddresses";

type Props = {
	emailAddressId?: string;
	routeModal: "" | "create" | "update" | "delete";
};

export const TableEmailAddresses = ({ emailAddressId, routeModal }: Props) => {
	const navigate = useNavigate();

	const { getUsername } = useToken();
	const username = getUsername();
	const includeSystem = username === "admin" || username === "dexteel";

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const [showModal, setShowModal] = useState<
		"" | "create" | "update" | "delete"
	>("");
	const [selectedRowData, setSelectedRowData] = useState<EmailAddress | null>(
		null,
	);
	const [error, setError] = useState("");

	const {
		data: addresses,
		isLoading: addressesLoading,
		isError: addressesIsError,
		error: addressesError,
	} = useEmailAddresses({ includeSystem });

	const selectEmailAddressNode = (address: EmailAddress) => {
		const node = gridApi?.getRowNode(String(address.EmailAddressId));

		if (node) {
			gridApi?.ensureNodeVisible(node);
			gridApi?.deselectAll();
			node.setSelected(true);
		}
	};

	const editEmailAddress = (address: EmailAddress, shouldNavigate = true) => {
		selectEmailAddressNode(address);
		setSelectedRowData(address);
		setShowModal("update");

		if (shouldNavigate) {
			navigate(`/configuration/email-addresses/${address.EmailAddressId}`);
		}
	};

	const handleDeleteEmailAddress = (address: EmailAddress) => {
		selectEmailAddressNode(address);
		setSelectedRowData(address);
		setShowModal("delete");
	};

	const handleNewEmailAddress = () => {
		gridApi?.deselectAll();
		setSelectedRowData(null);
		setShowModal("create");
		navigate(`/configuration/email-addresses/new`);
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useEmailAddressesOptionsFunctions({
		newEmailAddress: handleNewEmailAddress,
		editEmailAddress,
		deleteEmailAddress: handleDeleteEmailAddress,
	});

	const { columnDefs } = useTableEmailAddresses({
		editEmailAddress,
		deleteEmailAddress: handleDeleteEmailAddress,
		showContextMenu,
	});

	const defaultColDef: ColDef = {
		flex: 1,
		filter: false,
		floatingFilter: false,
		sortable: true,
		editable: false,
		resizable: false,
		wrapText: false,
		autoHeight: false,
		wrapHeaderText: true,
		suppressHeaderMenuButton: true,
		cellStyle: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const contextData = params.node?.data as EmailAddress | undefined;
			const nativeEvent = (
				params as GetContextMenuItemsParams & { event?: MouseEvent }
			).event;

			if (contextData && nativeEvent) {
				params.api.deselectAll();
				params.node?.setSelected(true);
				showContextMenu(
					nativeEvent as unknown as React.MouseEvent,
					contextData,
					"emailAddressContext",
				);
			}

			return [];
		},
		[showContextMenu],
	);

	const onModalHide = () => {
		gridApi?.deselectAll();
		setSelectedRowData(null);
		setShowModal("");
		navigate("/configuration/email-addresses");
	};

	useEffect(() => {
		registerConfig({
			id: "emailAddressContext",
			getOptions: getMenuOptions,
		});
	}, [registerConfig]);

	useEffect(() => {
		if (addressesIsError && addressesError) {
			setError(addressesError.message);
		}
	}, [addressesIsError, addressesError]);

	useEffect(() => {
		if (addressesLoading) {
			return;
		}

		if (routeModal === "") {
			setSelectedRowData(null);
			setShowModal("");
			gridApi?.deselectAll();
			return;
		}

		if (routeModal === "create") {
			setSelectedRowData(null);
			setShowModal("create");
			return;
		}

		if (!emailAddressId || !gridApi) {
			return;
		}

		const node: IRowNode | undefined = gridApi?.getRowNode(emailAddressId);

		if (node) {
			gridApi?.ensureNodeVisible(node);
			gridApi?.deselectAll();
			node.setSelected(true);
			setSelectedRowData(node.data as EmailAddress);
			setShowModal(routeModal);
			return;
		}

		onModalHide();
	}, [emailAddressId, gridApi, addressesLoading, routeModal, addresses]);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Email Addresses
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextField
						fullWidth
						label="Search"
						variant="outlined"
						size="small"
						margin="dense"
						value={filterValue}
						onChange={(event) => {
							setFilterValue(event.target.value);
							gridApi?.setGridOption("quickFilterText", event.target.value);
						}}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<IconButton edge="start" type="submit">
											<SearchIcon />
										</IconButton>
									</InputAdornment>
								),
							} as any,
						}}
					/>
				</Grid2>
				<Grid2
					container
					justifyContent="center"
					alignItems="center"
					sx={{ width: "100%" }}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper
							elevation={2}
							sx={{
								height: "71vh",
								width: "100%",
								"& .ag-status-bar": { minHeight: 36, alignItems: "center" },
							}}
						>
							<AgGridReact
								loading={addressesLoading}
								rowData={addresses ?? []}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={(
									event: RowDoubleClickedEvent<EmailAddress>,
								) => (event.data ? editEmailAddress(event.data) : null)}
								onGridReady={(params: GridReadyEvent<EmailAddress>) => {
									setGridApi(params.api);
								}}
								getRowId={(params) => String(params.data.EmailAddressId)}
								loadingOverlayComponent={CenteredLazyLoading}
								gridOptions={{
									theme: themeDXT,
									rowSelection: {
										mode: "singleRow" as const,
										checkboxes: false,
										enableClickSelection: true,
									},
									statusBar: {
										statusPanels: [
											{
												statusPanel: RecipientTypeLegend,
												align: "left",
											},
											{
												statusPanel: "agTotalRowCountComponent",
												align: "right",
											},
										],
									},
								}}
							/>
						</Paper>
					</Grid2>
					<Grid2 container justifyContent="flex-end">
						<Grid2
							size={{ md: 3, xs: 6 }}
							sx={{ display: "flex", justifyContent: "flex-end" }}
						>
							<Button
								variant="contained"
								color="primary"
								fullWidth
								onClick={() => handleNewEmailAddress()}
							>
								New Address
							</Button>
						</Grid2>
					</Grid2>
				</Grid2>
			</Grid2>
			<UpsertDeleteModal
				modal={showModal}
				onHide={onModalHide}
				address={selectedRowData}
				includeSystem={includeSystem}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
